<template>
  <div class="container">
    <h4 class="is-size-6-mobile is-size-5-tablet">
      {{ $t("codAdvanceReport.page_title") }}
    </h4>
    <p class="is-size-7 has-text-grey subtitle-note">{{ $t("codAdvanceReport.subtitle_note") }}</p>

    <!-- Summary cards -->
    <div class="box" v-if="summary && summary.available && summary.cards">
      <div class="columns is-multiline is-mobile cards">
        <div class="column is-6-mobile is-3-tablet" v-for="card in summaryCards" :key="card.key">
          <div class="card-tile" :class="card.tone">
            <p class="is-size-7 has-text-grey">{{ card.label }}</p>
            <p class="is-size-5 has-text-weight-bold">{{ card.value }}</p>
            <p class="is-size-7 has-text-grey" v-if="card.sub">{{ card.sub }}</p>
          </div>
        </div>
      </div>
      <p class="is-size-7 has-text-grey" v-if="summary.as_of">
        {{ $t("codAdvanceReport.as_of") }}: {{ formatDateTime(summary.as_of) }}
        <span v-if="summary.built_at"> &nbsp;|&nbsp; {{ $t("codAdvanceReport.built_at") }}: {{ formatDateTime(summary.built_at) }}</span>
        &nbsp;|&nbsp; {{ $t("codAdvanceReport.window") }}: {{ formatDate(summary.window.from) }} – {{ formatDate(summary.window.to) }}
        ({{ $t("codAdvanceReport.observed_days") }} {{ summary.observed_days }}, {{ $t("codAdvanceReport.reconstructed_days") }} {{ summary.reconstructed_days }})
      </p>
      <p class="is-size-7 has-text-warning-dark" v-if="summary.reconstructed_days > 0">
        {{ $t("codAdvanceReport.reconstructed_caveat") }}
      </p>
      <p class="is-size-7 has-text-danger" v-if="summary.missing_days && summary.missing_days.length">
        {{ $t("codAdvanceReport.missing_days") }}: {{ missingDaysLabel(summary.missing_days) }}
      </p>
      <p class="is-size-7 has-text-grey rules">
        {{ $t("codAdvanceReport.rules_title") }}:
        {{ $t("codAdvanceReport.rule_tenure", {days: summary.config.tenure_days}) }};
        {{ $t("codAdvanceReport.rule_continuity", {months: summary.config.continuity_months_required, bills: summary.config.min_monthly_bills}) }};
        {{ $t("codAdvanceReport.rule_history", {days: summary.config.min_observed_days}) }};
        {{ $t("codAdvanceReport.rule_base") }};
        {{ $t("codAdvanceReport.rule_rate", {rate: rateTiersLabel(summary.config.rate_tiers)}) }}
      </p>
    </div>

    <!-- Filters -->
    <div class="box">
      <div class="columns is-multiline is-mobile">
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <label class="label">{{ $t("codAdvanceReport.customer") }}:</label>
          <input class="input" type="text" v-model="q" :placeholder="$t('codAdvanceReport.search_placeholder')" @keyup.enter="search()" />
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <label class="label">{{ $t("codAdvanceReport.eligible_filter") }}:</label>
          <div class="select is-fullwidth">
            <select v-model="eligible">
              <option value="">{{ $t("codAdvanceReport.eligible_all") }}</option>
              <option value="1">{{ $t("codAdvanceReport.eligible_yes") }}</option>
              <option value="0">{{ $t("codAdvanceReport.eligible_no") }}</option>
            </select>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <label class="label">{{ $t("codAdvanceReport.sort") }}:</label>
          <div class="select is-fullwidth">
            <select v-model="sort">
              <option v-for="s in sortKeys" :key="s" :value="s">{{ $t("codAdvanceReport.sort_" + s) }}</option>
            </select>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <label class="label">&nbsp;</label>
          <div class="select is-fullwidth">
            <select v-model="dir">
              <option value="desc">{{ $t("codAdvanceReport.dir_desc") }}</option>
              <option value="asc">{{ $t("codAdvanceReport.dir_asc") }}</option>
            </select>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <label class="label is-invisible">{{ $t("codAdvanceReport.search") }}</label>
          <div class="buttons is-centered-mobile">
            <button class="button is-rounded is-fullwidth-mobile" @click="search()">
              <span class="icon"><i class="fal fa-search"></i></span>
              <span>{{ $t("codAdvanceReport.search") }}</span>
            </button>
            <button
              class="button is-primary is-rounded is-fullwidth-mobile"
              @click="exportExcel()"
              :disabled="!list || !list.available || exporting"
            >
              <span class="icon"><i class="fal fa-file-excel"></i></span>
              <span>{{ exporting ? $t("codAdvanceReport.exporting") : $t("codAdvanceReport.export_excel") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="loading" />

    <div class="notification is-warning is-light" v-if="list && !loading && !list.available">
      {{ $t("codAdvanceReport.unavailable") }}
    </div>

    <!-- The eligibility list -->
    <div class="box" v-if="list && !loading && list.available">
      <p class="is-size-7 has-text-grey">
        {{ $t("codAdvanceReport.total_rows") }}: {{ formatNumber(list.meta.total) }}
        &nbsp;|&nbsp; {{ $t("codAdvanceReport.definition_note") }}
      </p>
      <div class="table-container list-table">
        <table class="table is-bordered is-striped is-fullwidth is-narrow is-hoverable">
          <thead>
            <tr>
              <th class="has-text-centered">#</th>
              <th>{{ $t("codAdvanceReport.customer") }}</th>
              <th class="has-text-centered">{{ $t("codAdvanceReport.tenure_days") }}</th>
              <th class="has-text-centered">{{ $t("codAdvanceReport.active_months") }}</th>
              <th class="has-text-right">{{ $t("codAdvanceReport.outstanding_today") }}</th>
              <th class="has-text-right">{{ $t("codAdvanceReport.avg_30d") }}</th>
              <th class="has-text-centered">{{ $t("codAdvanceReport.days_basis") }}</th>
              <th class="has-text-right">{{ $t("codAdvanceReport.base_amount") }}</th>
              <!-- The base less the 90-day return rate — what the % applies to (2026-10-26). -->
              <th class="has-text-right" :title="$t('codAdvanceReport.rule_after_returns')">{{ $t("codAdvanceReport.base_after_returns") }}</th>
              <th class="has-text-centered">{{ $t("codAdvanceReport.rate") }}</th>
              <th class="has-text-right">{{ $t("codAdvanceReport.grant_preview") }}</th>
              <th class="has-text-centered">{{ $t("codAdvanceReport.dwell") }}</th>
              <th class="has-text-centered">{{ $t("codAdvanceReport.return_rate") }}</th>
              <th class="has-text-centered" :title="$t('codAdvanceReport.success_rate_hint')">{{ $t("codAdvanceReport.success_rate") }}</th>
              <th>{{ $t("codAdvanceReport.risk_flags") }}</th>
              <th class="has-text-centered" :title="$t('codAdvanceReport.reserved')">{{ $t("codAdvanceReport.outstanding_advance") }}</th>
              <th class="has-text-centered" :title="$t('codAdvanceReport.reserved')">{{ $t("codAdvanceReport.admin_debt") }}</th>
              <th class="has-text-centered" :title="$t('codAdvanceReport.reserved')">{{ $t("codAdvanceReport.contract_accepted") }}</th>
              <th class="has-text-centered">{{ $t("codAdvanceReport.eligible") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in rows"
              :key="row.customer_id"
              class="is-clickable"
              :class="{'is-selected-row': selectedCustomerId === row.customer_id, 'is-eligible-row': row.eligible_history}"
              @click="selectCustomer(row.customer_id)"
            >
              <td class="has-text-centered">{{ rowNumber(index) }}</td>
              <td>
                <strong>{{ row.name || ("#" + row.customer_id) }}</strong>
                <br /><span class="is-size-7 has-text-grey">#{{ row.customer_id }} · {{ row.tel || "-" }}</span>
                <button v-if="row.tel" class="button is-small is-white copy-tel" :title="$t('codAdvanceReport.copy_tel')" @click.stop="copyTel(row.tel)">
                  <span class="icon is-small"><i class="fal fa-copy"></i></span>
                </button>
              </td>
              <td class="has-text-centered">{{ formatNumber(row.tenure_days) }}</td>
              <td class="has-text-centered">
                {{ row.active_months_last_6 }}/6
                <span class="tag is-small" :class="row.continuity_ok ? 'is-success is-light' : 'is-danger is-light'">{{ row.continuity_ok ? "✓" : "✗" }}</span>
              </td>
              <td class="has-text-right">
                {{ formatNumber(row.outstanding_today) }}
                <br /><span class="is-size-7 has-text-grey">{{ formatNumber(row.outstanding_count_today) }} {{ $t("codAdvanceReport.bills_unit") }} · {{ $t("codAdvanceReport.pool") }} {{ formatNumber(row.pool_today) }}</span>
              </td>
              <td class="has-text-right">{{ formatNumber(row.avg_outstanding_30d) }}</td>
              <td class="has-text-centered">
                {{ row.observed_days_30d }}
                <span class="has-text-grey" v-if="row.reconstructed_days_30d">+{{ row.reconstructed_days_30d }}</span>
              </td>
              <td class="has-text-right"><strong>{{ formatNumber(row.base_amount) }}</strong></td>
              <td class="has-text-right">{{ formatNumber(row.base_after_returns) }}</td>
              <td class="has-text-centered">{{ row.rate_pct ? row.rate_pct + "%" : "—" }}</td>
              <td class="has-text-right"><strong>{{ row.rate_pct ? formatNumber(row.grant_preview) : "—" }}</strong></td>
              <td class="has-text-centered">{{ row.avg_dwell_days_30d }}</td>
              <td class="has-text-centered">{{ row.return_rate_90d }}%</td>
              <td class="has-text-centered" :title="successRateTitle(row.signed_3m, row.returned_3m)">
                <span :class="successRateClass(row.success_rate_3m)">{{ successRateLabel(row.success_rate_3m) }}</span>
              </td>
              <td>
                <span class="tag is-small is-warning is-light flag-tag" v-for="flag in row.risk_flags" :key="flag">{{ flagLabel(flag) }}</span>
              </td>
              <td class="has-text-centered has-text-grey">{{ reservedCell(row.outstanding_advance) }}</td>
              <td class="has-text-centered has-text-grey">{{ reservedCell(row.admin_debt) }}</td>
              <td class="has-text-centered has-text-grey">{{ reservedBool(row.contract_accepted) }}</td>
              <td class="has-text-centered">
                <span class="tag" :class="row.eligible_history ? 'is-success' : 'is-light'">{{ row.eligible_history ? $t("codAdvanceReport.yes") : $t("codAdvanceReport.no") }}</span>
              </td>
              <td class="has-text-centered">
                <button class="button is-small is-rounded" @click.stop="selectCustomer(row.customer_id)">{{ $t("codAdvanceReport.view") }}</button>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="20" class="has-text-centered has-text-grey">{{ $t("codAdvanceReport.empty_state") }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <pagination :pagination="list.meta" :offset="5" @paginate="onPaginate" v-if="list.meta.last_page > 1"></pagination>
    </div>

    <!-- Customer drill-down -->
    <div class="box" v-if="selectedCustomerId !== null" ref="drill">
      <div class="columns is-vcentered is-mobile drill-header">
        <div class="column">
          <h5 class="is-size-6">
            {{ $t("codAdvanceReport.drill_title") }}: {{ selectedCustomerName }}
            <span class="has-text-grey is-size-7" v-if="detail && detail.customer">
              (#{{ detail.customer.customer_id }} · {{ detail.customer.tel || "-" }})
              <button v-if="detail.customer.tel" class="button is-small is-white copy-tel" :title="$t('codAdvanceReport.copy_tel')" @click.stop="copyTel(detail.customer.tel)">
                <span class="icon is-small"><i class="fal fa-copy"></i></span>
              </button>
            </span>
          </h5>
        </div>
        <div class="column is-narrow">
          <button class="button is-small is-rounded" @click="closeCustomer()">
            <span class="icon"><i class="fal fa-times"></i></span>
            <span>{{ $t("codAdvanceReport.close") }}</span>
          </button>
        </div>
      </div>

      <Loading v-if="detailLoading" />

      <template v-if="detail && !detailLoading && detail.customer">
        <div class="columns is-multiline is-mobile cards">
          <div class="column is-6-mobile is-3-tablet" v-for="card in detailCards" :key="card.key">
            <div class="card-tile" :class="card.tone">
              <p class="is-size-7 has-text-grey">{{ card.label }}</p>
              <p class="is-size-6 has-text-weight-bold">{{ card.value }}</p>
              <p class="is-size-7 has-text-grey" v-if="card.sub">{{ card.sub }}</p>
            </div>
          </div>
        </div>
        <p class="is-size-7">
          <span class="tag is-small is-warning is-light flag-tag" v-for="flag in detail.customer.risk_flags" :key="flag">{{ flagLabel(flag) }}</span>
          <span class="tag is-small" :class="detail.customer.eligible_history ? 'is-success' : 'is-light'">
            {{ $t("codAdvanceReport.eligible") }}: {{ detail.customer.eligible_history ? $t("codAdvanceReport.yes") : $t("codAdvanceReport.no") }}
          </span>
        </p>

        <h6 class="is-size-7 has-text-weight-bold section-title">{{ $t("codAdvanceReport.daily_title") }}</h6>
        <p class="is-size-7 has-text-grey">{{ $t("codAdvanceReport.daily_hint") }}</p>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(dailyLabels, dailyDatasets, 300, 700)">
            <grouped-bar-chart
              :labels="dailyLabels"
              :datasets="dailyDatasets"
              :lines="dailyLines"
              :tooltip-notes="dailyNotes"
              :unit="$t('codAdvanceReport.kip_unit')"
              :format-value="formatNumber"
            />
          </div>
        </div>

        <h6 class="is-size-7 has-text-weight-bold section-title">{{ $t("codAdvanceReport.monthly_title") }}</h6>
        <p class="is-size-7 has-text-grey">{{ $t("codAdvanceReport.monthly_hint") }}</p>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(monthlyLabels, monthlyDatasets, 260, 600)">
            <grouped-bar-chart
              :labels="monthlyLabels"
              :datasets="monthlyDatasets"
              :lines="monthlyLines"
              :unit="$t('codAdvanceReport.bills_unit')"
              :format-value="formatNumber"
            />
          </div>
        </div>

        <div class="columns">
          <div class="column is-7">
            <div class="table-container daily-table">
              <table class="table is-bordered is-striped is-fullwidth is-narrow">
                <thead>
                  <tr>
                    <th>{{ $t("codAdvanceReport.date") }}</th>
                    <th class="has-text-centered">{{ $t("codAdvanceReport.source") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.outstanding") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.pool") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.arrived_day") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.signed_day") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.returned_day") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in detail.daily" :key="d.day" :class="{'is-missing-row': !d.built}">
                    <td>{{ formatDate(d.day) }}</td>
                    <td class="has-text-centered">
                      <span v-if="!d.built" class="tag is-danger is-light is-small">{{ $t("codAdvanceReport.not_built") }}</span>
                      <span v-else-if="d.source === 'reconstructed'" class="tag is-warning is-light is-small">{{ $t("codAdvanceReport.source_reconstructed") }}</span>
                      <span v-else class="tag is-success is-light is-small">{{ $t("codAdvanceReport.source_observed") }}</span>
                    </td>
                    <td class="has-text-right">{{ d.built ? formatNumber(d.outstanding_amount) + " (" + formatNumber(d.outstanding_count) + ")" : "-" }}</td>
                    <td class="has-text-right">{{ d.built ? formatNumber(d.pool_amount) + " (" + formatNumber(d.pool_count) + ")" : "-" }}</td>
                    <td class="has-text-right">{{ d.built ? formatNumber(d.arrived_amount_day) + " (" + formatNumber(d.arrived_count_day) + ")" : "-" }}</td>
                    <td class="has-text-right">{{ d.built ? formatNumber(d.signed_amount_day) + " (" + formatNumber(d.signed_count_day) + ")" : "-" }}</td>
                    <td class="has-text-right">{{ d.built ? formatNumber(d.returned_amount_day) + " (" + formatNumber(d.returned_count_day) + ")" : "-" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="column is-5">
            <div class="table-container">
              <table class="table is-bordered is-striped is-fullwidth is-narrow">
                <thead>
                  <tr>
                    <th>{{ $t("codAdvanceReport.month") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.bills_created") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.bills_signed") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.bills_returned") }}</th>
                    <th class="has-text-right" :title="$t('codAdvanceReport.success_rate_hint')">{{ $t("codAdvanceReport.success_rate_month") }}</th>
                    <th class="has-text-right">{{ $t("codAdvanceReport.avg_dwell_month") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in detail.monthly" :key="m.month" :class="{'is-empty-row': m.cod_bills < minMonthlyBills}">
                    <td>{{ m.month }}</td>
                    <td class="has-text-right">{{ formatNumber(m.cod_bills) }}</td>
                    <td class="has-text-right">{{ formatNumber(m.signed_bills) }}</td>
                    <td class="has-text-right">{{ formatNumber(m.returned_bills) }}</td>
                    <td class="has-text-right" :title="successRateTitle(m.signed_bills, m.returned_bills)">
                      <span :class="successRateClass(m.success_rate)">{{ successRateLabel(m.success_rate) }}</span>
                    </td>
                    <td class="has-text-right">{{ m.avg_dwell_days === null ? "-" : m.avg_dwell_days }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <h6 class="is-size-7 has-text-weight-bold section-title">{{ $t("codAdvanceReport.bills_title") }} ({{ formatNumber(detail.bills.length) }})</h6>
        <p class="is-size-7 has-text-grey">{{ $t("codAdvanceReport.bills_hint") }}</p>
        <div class="table-container bills-table">
          <table class="table is-bordered is-striped is-fullwidth is-narrow">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t("codAdvanceReport.shipment_number") }}</th>
                <th class="has-text-right">{{ $t("codAdvanceReport.price") }}</th>
                <th>{{ $t("codAdvanceReport.arrived_at") }}</th>
                <th class="has-text-centered">{{ $t("codAdvanceReport.dwell_days") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(b, i) in detail.bills" :key="b.id">
                <td>{{ i + 1 }}</td>
                <td>{{ b.shipment_number || b.id }}</td>
                <td class="has-text-right">{{ formatNumber(b.total_price) }}</td>
                <td>{{ formatDateTime(b.arrived_at) }}</td>
                <td class="has-text-centered">{{ b.dwell_days }}</td>
              </tr>
              <tr v-if="!detail.bills.length">
                <td colspan="5" class="has-text-centered has-text-grey">{{ $t("codAdvanceReport.empty_state") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Loading from "@/views/Loading";
import XLSX from "xlsx";
import GroupedBarChart from "@/components/Chart/GroupedBarChart";
import Pagination from "@/Plugins/Paginations/Pagination";
import {
  fetchCodAdvanceEligibilityDetail,
  fetchCodAdvanceEligibilityList,
  fetchCodAdvanceEligibilitySummary,
} from "@/services/codAdvanceReportService";

const COLORS = {outstanding: "#2EA8D9", today: "#E11F26", average: "#111827", bills: "#2EA8D9", threshold: "#E11F26"};
const SORT_KEYS = ["base_amount", "outstanding_today", "avg_outstanding_30d", "tenure_days", "pool_today", "grant_preview", "customer_id"];
const EXPORT_CAP = 5000;
const EXPORT_PAGE = 200;

export default {
  name: "codAdvanceEligibilityReport",
  components: {Loading, GroupedBarChart, Pagination},
  data() {
    return {
      q: "",
      eligible: "",
      sort: "base_amount",
      dir: "desc",
      sortKeys: SORT_KEYS,
      page: 1,
      perPage: 50,
      list: null,
      summary: null,
      loading: false,
      requestSeq: 0,
      exporting: false,
      selectedCustomerId: null,
      detail: null,
      detailLoading: false,
      detailSeq: 0,
    };
  },
  computed: {
    rows() {
      return this.list && this.list.data ? this.list.data : [];
    },
    minMonthlyBills() {
      const cfg = (this.detail && this.detail.config) || (this.summary && this.summary.config) || {};
      return cfg.min_monthly_bills || 0;
    },
    summaryCards() {
      if (!this.summary || !this.summary.cards) return [];
      const c = this.summary.cards;
      const t = (k) => this.$t("codAdvanceReport." + k);
      return [
        {key: "eligible", label: t("card_eligible"), value: this.formatNumber(c.eligible), sub: t("card_customers") + ": " + this.formatNumber(c.customers), tone: "is-tone-success"},
        {key: "base", label: t("card_base_sum"), value: this.formatMoney(c.eligible_base_amount), tone: ""},
        {key: "grant", label: t("card_grant_sum"), value: this.formatMoney(c.eligible_grant_preview), tone: "is-tone-primary"},
        {key: "dwell", label: t("card_avg_dwell"), value: c.avg_dwell_days_30d === null ? "-" : c.avg_dwell_days_30d + " " + t("day_unit"), tone: ""},
        {key: "outstanding", label: t("card_outstanding_today"), value: this.formatMoney(c.outstanding_today), sub: this.formatNumber(c.outstanding_count_today) + " " + t("bills_unit"), tone: ""},
        {key: "pool", label: t("card_pool_today"), value: this.formatMoney(c.pool_today), sub: t("card_customers_with_pool") + ": " + this.formatNumber(c.customers_with_pool), tone: ""},
        {key: "blocked", label: t("card_blocked"), value: this.formatNumber(c.blocked.tenure) + " / " + this.formatNumber(c.blocked.continuity) + " / " + this.formatNumber(c.blocked.history), sub: t("card_blocked_tenure") + " / " + t("card_blocked_continuity") + " / " + t("card_blocked_history"), tone: "is-tone-warning"},
        {key: "flags", label: t("card_flags"), value: this.formatNumber(c.flags.dwell_gt_7d) + " / " + this.formatNumber(c.flags.return_rate_gt_10pct) + " / " + this.formatNumber(c.flags.volume_drop), sub: t("flag_dwell_gt_7d") + " / " + t("flag_return_rate_gt_10pct") + " / " + t("flag_volume_drop"), tone: "is-tone-warning"},
      ];
    },
    selectedCustomerName() {
      if (this.detail && this.detail.customer) return this.detail.customer.name || "#" + this.detail.customer.customer_id;
      const row = this.rows.find((r) => r.customer_id === this.selectedCustomerId);
      return row ? row.name || "#" + row.customer_id : "#" + this.selectedCustomerId;
    },
    detailCards() {
      if (!this.detail || !this.detail.customer) return [];
      const c = this.detail.customer;
      const t = (k) => this.$t("codAdvanceReport." + k);
      return [
        {key: "tenure", label: t("tenure_days"), value: this.formatNumber(c.tenure_days) + " " + t("day_unit"), sub: c.first_cod_shipment_at ? t("first_bill") + " " + this.formatDate(c.first_cod_shipment_at) : "", tone: ""},
        {key: "months", label: t("active_months"), value: c.active_months_last_6 + "/6", sub: t("continuity") + ": " + (c.continuity_ok ? t("yes") : t("no")), tone: c.continuity_ok ? "is-tone-success" : "is-tone-warning"},
        {key: "outstanding", label: t("outstanding_today"), value: this.formatMoney(c.outstanding_today), sub: this.formatNumber(c.outstanding_count_today) + " " + t("bills_unit") + " · " + t("pool") + " " + this.formatNumber(c.pool_today), tone: ""},
        {key: "avg", label: t("avg_30d"), value: this.formatMoney(c.avg_outstanding_30d), sub: t("days_basis") + ": " + c.observed_days_30d + (c.reconstructed_days_30d ? " +" + c.reconstructed_days_30d : "") + " · " + t("min_30d") + " " + this.formatNumber(c.min_outstanding_30d), tone: ""},
        {key: "base", label: t("base_amount"), value: this.formatMoney(c.base_amount), sub: t("rule_base"), tone: "is-tone-primary"},
        {key: "grant", label: t("grant_preview"), value: c.rate_pct ? this.formatMoney(c.grant_preview) : "—", sub: c.rate_pct ? this.formatNumber(c.base_after_returns) + " (" + t("base_after_returns") + ") × " + c.rate_pct + "%" : t("no_rate"), tone: "is-tone-primary"},
        {key: "dwell", label: t("dwell"), value: c.avg_dwell_days_30d + " " + t("day_unit"), sub: t("return_rate") + " " + c.return_rate_90d + "% · " + t("success_rate") + " " + this.successRateLabel(c.success_rate_3m), tone: ""},
        {key: "last", label: t("last_bill"), value: c.days_since_last_cod_bill === null ? "-" : c.days_since_last_cod_bill + " " + t("day_unit"), sub: t("as_of") + " " + this.formatDateTime(c.as_of), tone: ""},
      ];
    },
    // Chart inputs are COMPUTED (stable references): GroupedBarChart destroys +
    // recreates on every labels/datasets change, so a method call in the
    // template would redraw on every render.
    dailyLabels() {
      return this.detail ? this.detail.daily.map((d) => moment(d.day).format("DD/MM")).concat([this.$t("codAdvanceReport.today_marker")]) : [];
    },
    dailyDatasets() {
      if (!this.detail || !this.detail.customer) return [];
      const daily = this.detail.daily;
      return [
        {
          label: this.$t("codAdvanceReport.outstanding"),
          color: COLORS.outstanding,
          values: daily.map((d) => (d.built ? d.outstanding_amount : null)).concat([null]),
        },
        {
          label: this.$t("codAdvanceReport.today_marker"),
          color: COLORS.today,
          values: daily.map(() => null).concat([this.detail.customer.outstanding_today]),
        },
      ];
    },
    dailyLines() {
      if (!this.detail || !this.detail.customer) return [];
      const n = this.detail.daily.length + 1;
      return [{label: this.$t("codAdvanceReport.average_line"), color: COLORS.average, values: Array(n).fill(this.detail.customer.avg_outstanding_30d)}];
    },
    dailyNotes() {
      if (!this.detail) return null;
      const t = (k) => this.$t("codAdvanceReport." + k);
      const notes = this.detail.daily.map((d) => (!d.built ? t("not_built") : d.source === "reconstructed" ? t("source_reconstructed") : t("source_observed")));
      return [notes.concat([t("live_position")]), null];
    },
    monthlyLabels() {
      return this.detail ? this.detail.monthly.map((m) => m.month) : [];
    },
    monthlyDatasets() {
      if (!this.detail) return [];
      return [{label: this.$t("codAdvanceReport.bills_created"), color: COLORS.bills, values: this.detail.monthly.map((m) => m.cod_bills)}];
    },
    monthlyLines() {
      if (!this.detail) return [];
      return [{label: this.$t("codAdvanceReport.threshold_line", {bills: this.minMonthlyBills}), color: COLORS.threshold, values: this.detail.monthly.map(() => this.minMonthlyBills)}];
    },
  },
  methods: {
    rowNumber(index) {
      const meta = this.list && this.list.meta;
      return (meta && meta.from ? meta.from : 1) + index;
    },
    flagLabel(flag) {
      const key = "codAdvanceReport.flag_" + flag;
      const label = this.$t(key);
      return label === key ? flag : label;
    },
    reservedCell(value) {
      return value === null || value === undefined ? "—" : this.formatNumber(value);
    },
    // ອັດຕາສຳເລັດ = signed ÷ (signed + returned); null = nothing reached an outcome yet.
    successRateLabel(value) {
      return value === null || value === undefined ? "—" : value + "%";
    },
    successRateClass(value) {
      if (value === null || value === undefined) return "has-text-grey";
      return value >= 90 ? "has-text-success" : value < 80 ? "has-text-danger" : "";
    },
    successRateTitle(signed, returned) {
      return this.$t("codAdvanceReport.success_rate_basis", {signed: this.formatNumber(signed), returned: this.formatNumber(returned)});
    },
    copyTel(tel) {
      const text = String(tel || "").trim();
      if (!text) return;
      const done = () => this.$vs.notification({color: "success", title: this.$t("codAdvanceReport.copied"), text});
      const fail = () => this.$vs.notification({color: "danger", title: this.$t("codAdvanceReport.copy_failed")});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => (this.copyTelFallback(text) ? done() : fail()));
        return;
      }
      this.copyTelFallback(text) ? done() : fail();
    },
    // http:// origins (no secure context) have no navigator.clipboard — a hidden textarea + execCommand still works there.
    copyTelFallback(text) {
      try {
        const area = document.createElement("textarea");
        area.value = text;
        area.setAttribute("readonly", "");
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(area);
        return ok;
      } catch (e) {
        return false;
      }
    },
    reservedBool(value) {
      if (value === null || value === undefined) return "—";
      return value ? this.$t("codAdvanceReport.yes") : this.$t("codAdvanceReport.no");
    },
    rateTiersLabel(tiers) {
      return (tiers || []).map((t) => t.rate_pct + "% (≥" + t.min_tenure_days + " " + this.$t("codAdvanceReport.day_unit") + ")").join(", ");
    },
    missingDaysLabel(days) {
      const shown = days.slice(0, 8).map((d) => this.formatDate(d));
      return shown.join(", ") + (days.length > 8 ? " … (+" + (days.length - 8) + ")" : "");
    },
    chartStyle(labels, datasets, baseHeight, baseMinWidth) {
      const groups = labels ? labels.length : 0;
      const series = datasets && datasets.length ? datasets.length : 1;
      const groupWidth = Math.max(18 * series + 14, 40);
      // 45°-rotated axis labels need ~5px of height per character (same rule as
      // the transit and creation reports), capped so a long label cannot push
      // the chart off-screen.
      const longestLabel = (labels || []).reduce((max, l) => Math.max(max, String(l).length), 0);
      const labelRoom = Math.min(Math.round(longestLabel * 5), 160);
      return {
        height: baseHeight + labelRoom + "px",
        minWidth: Math.max(baseMinWidth, groups * groupWidth + 140) + "px",
      };
    },
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "-";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatMoney(value) {
      return this.formatNumber(value || 0) + " " + this.$t("codAdvanceReport.kip_unit");
    },
    params(page, perPage) {
      const p = {sort: this.sort, dir: this.dir, page, per_page: perPage};
      if (this.q.trim() !== "") p.q = this.q.trim();
      if (this.eligible !== "") p.eligible = this.eligible;
      return p;
    },
    search() {
      this.page = 1;
      this.fetchList();
    },
    onPaginate(page) {
      this.page = page;
      this.fetchList();
    },
    fetchList() {
      const seq = ++this.requestSeq;
      this.loading = true;
      fetchCodAdvanceEligibilityList(this.params(this.page, this.perPage))
        .then((data) => {
          if (seq !== this.requestSeq) return;
          this.list = data;
          this.loading = false;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          console.log(error);
          this.$vs.notification({color: "danger", title: this.$t("codAdvanceReport.error_loading")});
          this.loading = false;
        });
    },
    fetchSummary() {
      fetchCodAdvanceEligibilitySummary()
        .then((data) => {
          this.summary = data;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    selectCustomer(customerId) {
      this.selectedCustomerId = customerId;
      this.fetchDetail();
      this.$nextTick(() => {
        if (this.$refs.drill) this.$refs.drill.scrollIntoView({behavior: "smooth", block: "start"});
      });
    },
    closeCustomer() {
      this.detailSeq++; // an in-flight detail response must not reopen the box
      this.selectedCustomerId = null;
      this.detail = null;
      this.detailLoading = false;
    },
    fetchDetail() {
      if (this.selectedCustomerId === null) return;
      const seq = ++this.detailSeq;
      this.detail = null; // never show customer A's numbers under customer B's title
      this.detailLoading = true;
      fetchCodAdvanceEligibilityDetail(this.selectedCustomerId)
        .then((data) => {
          if (seq !== this.detailSeq) return;
          this.detail = data;
          this.detailLoading = false;
        })
        .catch((error) => {
          if (seq !== this.detailSeq) return;
          console.log(error);
          this.$vs.notification({color: "danger", title: this.$t("codAdvanceReport.error_loading")});
          this.detailLoading = false;
        });
    },
    customerToSheet(r) {
      const t = (k) => this.$t("codAdvanceReport." + k);
      return {
        [t("customer_id")]: r.customer_id,
        [t("name")]: r.name || "",
        [t("tel")]: r.tel || "",
        [t("eligible")]: r.eligible_history ? t("yes") : t("no"),
        [t("tenure_days")]: r.tenure_days,
        [t("active_months")]: r.active_months_last_6,
        [t("continuity")]: r.continuity_ok ? t("yes") : t("no"),
        [t("outstanding_today")]: r.outstanding_today,
        [t("outstanding_count")]: r.outstanding_count_today,
        [t("pool")]: r.pool_today,
        [t("avg_30d")]: r.avg_outstanding_30d,
        [t("min_30d")]: r.min_outstanding_30d,
        [t("observed_days")]: r.observed_days_30d,
        [t("reconstructed_days")]: r.reconstructed_days_30d,
        [t("base_amount")]: r.base_amount,
        [t("base_after_returns")]: r.base_after_returns,
        [t("rate")]: r.rate_pct,
        [t("grant_preview")]: r.grant_preview,
        [t("dwell")]: r.avg_dwell_days_30d,
        [t("return_rate")]: r.return_rate_90d,
        [t("success_rate")]: r.success_rate_3m === null || r.success_rate_3m === undefined ? "" : r.success_rate_3m,
        [t("signed_3m")]: r.signed_3m,
        [t("returned_3m")]: r.returned_3m,
        [t("last_bill")]: r.days_since_last_cod_bill === null ? "" : r.days_since_last_cod_bill,
        [t("risk_flags")]: (r.risk_flags || []).map((f) => this.flagLabel(f)).join(", "),
        [t("as_of")]: this.formatDateTime(r.as_of),
      };
    },
    async exportExcel() {
      if (!this.list || !this.list.available || this.exporting) return;
      const t = (k) => this.$t("codAdvanceReport." + k);
      this.exporting = true;
      try {
        // The current filter, every page, capped — never the id documents (the API does not send them).
        const all = [];
        let page = 1;
        let lastPage = 1;
        let envelope = this.list;
        do {
          envelope = await fetchCodAdvanceEligibilityList(this.params(page, EXPORT_PAGE));
          all.push(...(envelope.data || []));
          lastPage = envelope.meta ? envelope.meta.last_page : 1;
          page++;
        } while (page <= lastPage && all.length < EXPORT_CAP);
        const rows = all.slice(0, EXPORT_CAP);

        const wb = XLSX.utils.book_new();
        const info = [
          {[t("info_key")]: t("as_of"), [t("info_value")]: this.formatDateTime(envelope.as_of)},
          {[t("info_key")]: t("window"), [t("info_value")]: this.formatDate(envelope.window.from) + " – " + this.formatDate(envelope.window.to)},
          {[t("info_key")]: t("observed_days"), [t("info_value")]: envelope.observed_days},
          {[t("info_key")]: t("reconstructed_days"), [t("info_value")]: envelope.reconstructed_days},
          {[t("info_key")]: t("customer"), [t("info_value")]: this.q || "-"},
          {[t("info_key")]: t("eligible_filter"), [t("info_value")]: this.eligible === "" ? t("eligible_all") : this.eligible === "1" ? t("eligible_yes") : t("eligible_no")},
          {[t("info_key")]: t("sort"), [t("info_value")]: t("sort_" + this.sort) + " " + t("dir_" + this.dir)},
          {[t("info_key")]: t("total_rows"), [t("info_value")]: envelope.meta ? envelope.meta.total : rows.length},
          {[t("info_key")]: t("rules_title"), [t("info_value")]: t("rule_tenure", {days: envelope.config.tenure_days}) + "; " + t("rule_continuity", {months: envelope.config.continuity_months_required, bills: envelope.config.min_monthly_bills}) + "; " + t("rule_history", {days: envelope.config.min_observed_days}) + "; " + t("rule_base") + "; " + t("rule_rate", {rate: this.rateTiersLabel(envelope.config.rate_tiers)})},
        ];
        if (envelope.reconstructed_days > 0) info.push({[t("info_key")]: t("warning"), [t("info_value")]: t("reconstructed_caveat")});
        if (envelope.missing_days && envelope.missing_days.length) info.push({[t("info_key")]: t("warning"), [t("info_value")]: t("missing_days") + ": " + envelope.missing_days.map((d) => this.formatDate(d)).join(", ")});
        if (all.length > EXPORT_CAP || (envelope.meta && envelope.meta.total > EXPORT_CAP)) info.push({[t("info_key")]: t("warning"), [t("info_value")]: t("export_cap_note", {cap: EXPORT_CAP})});
        info.push({[t("info_key")]: t("definition_note"), [t("info_value")]: ""});
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(info), "Info");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.map((r) => this.customerToSheet(r))), "Customers");

        if (this.detail && this.detail.customer && this.selectedCustomerId !== null) {
          const id = String(this.selectedCustomerId);
          XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(this.detail.daily.map((d) => ({
              [t("date")]: this.formatDate(d.day),
              [t("source")]: !d.built ? t("not_built") : d.source === "reconstructed" ? t("source_reconstructed") : t("source_observed"),
              [t("outstanding")]: d.built ? d.outstanding_amount : "",
              [t("outstanding_count")]: d.built ? d.outstanding_count : "",
              [t("pool")]: d.built ? d.pool_amount : "",
              [t("arrived_day")]: d.built ? d.arrived_amount_day : "",
              [t("signed_day")]: d.built ? d.signed_amount_day : "",
              [t("returned_day")]: d.built ? d.returned_amount_day : "",
            }))),
            ("Daily-" + id).substring(0, 31)
          );
          XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(this.detail.monthly.map((m) => ({
              [t("month")]: m.month,
              [t("bills_created")]: m.cod_bills,
              [t("cod_amount")]: m.cod_amount,
              [t("bills_signed")]: m.signed_bills,
              [t("signed_amount")]: m.signed_amount,
              [t("bills_returned")]: m.returned_bills,
              [t("success_rate_month")]: m.success_rate === null || m.success_rate === undefined ? "" : m.success_rate,
              [t("avg_dwell_month")]: m.avg_dwell_days === null ? "" : m.avg_dwell_days,
            }))),
            ("Monthly-" + id).substring(0, 31)
          );
          XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(this.detail.bills.map((b) => ({
              [t("shipment_number")]: b.shipment_number || b.id,
              [t("price")]: b.total_price,
              [t("arrived_at")]: this.formatDateTime(b.arrived_at),
              [t("dwell_days")]: b.dwell_days,
            }))),
            ("Bills-" + id).substring(0, 31)
          );
        }

        XLSX.writeFile(wb, "cod-advance-eligibility " + this.formatDate(envelope.as_of || new Date()).replace(/\//g, "-") + ".xlsx");
      } catch (error) {
        console.log(error);
        this.$vs.notification({color: "danger", title: this.$t("codAdvanceReport.error_loading")});
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.fetchSummary();
    this.fetchList();
  },
};
</script>

<style lang="scss" scoped>
.subtitle-note {
  margin-bottom: 1rem;
}

.chart-scroll {
  overflow-x: auto;
  margin-bottom: 1rem;
}

.chart-wrapper {
  position: relative;
  // vue-chartjs renders <div><canvas width=400 height=400> with NO height on its
  // div, so Chart.js (responsive, maintainAspectRatio: false) keeps the 400px
  // canvas default instead of this wrapper's height. A shorter wrapper then
  // hides the x-axis labels behind a vertical scrollbar. Hand the chart root the
  // wrapper's height so the plot always fits exactly.
  > div {
    height: 100%;
  }
}

.cards {
  margin-bottom: 0.25rem;
}

.card-tile {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
  height: 100%;
  background: #fafafa;

  &.is-tone-success {
    border-color: #b7e4c7;
    background: #f1faf4;
  }

  &.is-tone-primary {
    border-color: #bfe3f4;
    background: #eef8fc;
  }

  &.is-tone-warning {
    border-color: #f3dfa2;
    background: #fdf8e7;
  }
}

.rules {
  margin-top: 0.5rem;
}

.is-clickable {
  cursor: pointer;
}

.is-selected-row {
  background-color: #fff8e6 !important;
}

.is-eligible-row td:first-child {
  border-left: 3px solid #48c78e;
}

.is-missing-row td {
  background-color: #fff5f5;
}

.is-empty-row td {
  color: #a0a0a0;
}

.flag-tag {
  margin-right: 0.25rem;
  margin-bottom: 0.15rem;
}

.copy-tel {
  height: 1.5em;
  padding: 0 0.3rem;
  margin-left: 0.25rem;
  vertical-align: middle;
}

.list-table {
  max-height: 640px;
  overflow-y: auto;
}

.daily-table {
  max-height: 420px;
  overflow-y: auto;
}

.bills-table {
  max-height: 360px;
  overflow-y: auto;
}

.drill-header {
  margin-bottom: 0.5rem;
}

.section-title {
  margin-top: 1rem;
  margin-bottom: 0.25rem;
}

.box {
  @media (max-width: 768px) {
    padding: 1rem;
  }
}

.buttons {
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;

    .button {
      margin-bottom: 0.5rem;
      width: 100%;
    }
  }

  @media (min-width: 769px) {
    .button {
      margin-right: 0.5rem;
    }
  }
}
</style>
